// Two series primitives for the context behind a rating, which the chart
// previously kept to itself (MODEL_BOOK_EVOLUTION.md §6.8–6.9):
//
//   - VcpRunupOverlay shades the pre-base run-up on the price pane and writes
//     the excess return over the market on it. The late-stage penalty is the
//     largest single negative in the rating table (−15.9% median 26w) and was
//     a number on a badge with nothing on the chart to point at. Amber when
//     the DETECTOR applied the late-stage penalty, neutral otherwise — its
//     own verdict, not a threshold test here, because the penalty is waived
//     for elite-RS names and a threshold alone would flag a penalty the
//     rating never applied.
//
//   - VcpEdgeVolumeOverlay shades the right-edge volume window behind the
//     VOLUME BARS and writes the 10/50 ratio on it. "Volume dried up into the
//     pivot" is the claim the grey V badge makes; this is where. Green inside
//     the calibrated band, neutral outside — the band DESCRIBES rather than
//     scores, so being outside it is not a failure and is not coloured as
//     one.
//
//     There is no volume pane: the histogram is an overlay on its own price
//     scale ('volume') inside the PRICE pane, with margins the chart
//     controller moves when the RS line is drawn. So the region is bounded
//     vertically by the volume series' own coordinates — its zero line and
//     the tallest visible bar — rather than the pane, or it would wash over
//     the candles; and not by a copy of the margins, which would drift.
//
// Neither keeps a copy of a threshold: the band and the late-stage verdict
// arrive in the payload from VcpChartOverlay, which reads them off
// VcpDetector. A
// region is drawn only when its measurement is present, so presence is the
// gate (the same contract the outcome fields use).
//
// Both are detector-measured, so labels and edges are SOLID (VCP rule 6).
//
// Attached via series.attachPrimitive(...) — the run-up to the price series,
// the volume one to the volume histogram — from vcp_annotations_controller.

import { bandExtent } from "./vcp_base_overlay"
import { readTheme, drawLabel, labelFits, LABEL_INSET_PX } from "./vcp_markup_overlay"

export const RUNUP_FILL = { normal: "rgba(120, 144, 156, 0.10)", late: "rgba(255, 152, 0, 0.14)" }
export const RUNUP_INK = {
  normal: { light: "#546e7a", dark: "#b0bec5" },
  late:   { light: "#e65100", dark: "#ffb74d" }
}

export const EDGE_VOL_FILL = { inBand: "rgba(76, 175, 80, 0.16)", outOfBand: "rgba(120, 144, 156, 0.12)" }
export const EDGE_VOL_INK = {
  inBand:    { light: "#2e7d32", dark: "#81c784" },
  outOfBand: { light: "#546e7a", dark: "#b0bec5" }
}

// Number(null) is 0 and Number('') is 0, both finite — so a plain
// Number.isFinite check reads a MISSING measurement as zero, which here means
// labelling a region "Run-up +0% vs market". Absent and zero are different
// answers.
function numeric(value) {
  if (value === null || value === undefined || value === "") return null

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

// "+74% vs market" / "−8% vs market". A run-up is an EXCESS return over the
// benchmark, so the label says so rather than reading as a bare gain.
export function runupLabel(pct) {
  const value = numeric(pct)
  if (value === null) return null

  const sign = value >= 0 ? "+" : "−"
  return `Run-up ${sign}${Math.abs(value * 100).toFixed(0)}% vs market`
}

// "Edge vol 0.62×" — the 10-bar average over the 50-bar one.
export function edgeVolLabel(ratio) {
  const value = numeric(ratio)
  if (value === null || value <= 0) return null

  return `Edge vol ${value.toFixed(2)}×`
}

export function isInBand(ratio, band) {
  const value = numeric(ratio)
  if (value === null || !Array.isArray(band) || band.length !== 2) return false

  const low = numeric(band[0])
  const high = numeric(band[1])
  if (low === null || high === null) return false

  return value >= low && value <= high
}

// Gap between the volume band's top and its label's centre line.
const VOLUME_LABEL_GAP_PX = 9

// The vertical span the volume histogram occupies on screen: from its zero
// line up to the tallest bar in the visible range, in media pixels. Read off
// the volume series' own price-to-coordinate mapping, so it follows whatever
// scale margins the chart controller has applied (they move when the RS line
// is drawn). Null when the series cannot place the bars yet.
export function volumeBand(timeScale, series, data, height) {
  if (!series || !Array.isArray(data) || data.length === 0) return null

  const bottom = series.priceToCoordinate(0)
  if (bottom === null || bottom === undefined) return null

  const range = timeScale.getVisibleLogicalRange?.()
  const first = Math.max(0, Math.floor(range?.from ?? 0))
  const last = Math.min(data.length - 1, Math.ceil(range?.to ?? data.length - 1))
  let peak = 0
  for (let i = first; i <= last; i++) peak = Math.max(peak, Number(data[i]?.volume) || 0)
  if (peak <= 0) return null

  const top = series.priceToCoordinate(peak)
  if (top === null || top === undefined) return null

  return { top: Math.max(0, Math.min(top, bottom)), bottom: Math.min(height, Math.max(top, bottom)) }
}

// Shared shape: fill the region, then put the label at its top left. A
// price-pane region (`vertical: "pane"`) runs the full height with its label
// at `labelY`; a volume region (`vertical: "volume"`) is clipped to the
// histogram's own band with the label just above it, clear of the candles
// and of the pivot readout that owns the top right.
class RegionRenderer {
  constructor(data) {
    this._data = data
  }

  draw(target) {
    const { chart, series, data, from, to, stopBefore, fill, ink, label, theme, labelY, vertical } = this._data
    if (!chart || !data || data.length === 0 || !from || !to || !fill) return

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context
      const timeScale = chart.timeScale()
      const extent = bandExtent(timeScale, data, from, to)
      if (!extent) return

      let right = extent.x2
      // Abut a neighbouring region rather than overlap it: see VcpRunupOverlay.
      if (stopBefore) {
        const neighbour = bandExtent(timeScale, data, stopBefore, stopBefore)
        if (neighbour) right = Math.min(right, neighbour.x1)
      }

      const x1 = Math.max(0, extent.x1)
      const x2 = Math.min(scope.mediaSize.width, right)
      if (x2 <= x1) return

      let top = 0
      let bottom = scope.mediaSize.height
      let textY = labelY
      if (vertical === "volume") {
        const band = volumeBand(timeScale, series, data, scope.mediaSize.height)
        if (!band) return
        top = band.top
        bottom = band.bottom
        textY = Math.max(VOLUME_LABEL_GAP_PX, top - VOLUME_LABEL_GAP_PX)
      }

      ctx.fillStyle = fill
      ctx.fillRect(x1, top, x2 - x1, bottom - top)

      if (!label || !labelFits(ctx, label, x2 - x1)) return
      drawLabel(ctx, label, x1 + LABEL_INSET_PX, textY, theme.dark ? ink.dark : ink.light, theme.background)
    })
  }
}

class RegionPaneView {
  constructor(data) { this._data = data }

  update(data) { this._data = data }
  renderer() { return new RegionRenderer(this._data) }
  zOrder() { return "bottom" }
}

// Common lifecycle: hold the chart, re-read theme colours on attach and on
// theme:changed (never per frame — getComputedStyle in a renderer runs twice
// per pan tick), and nothing else. Neither region has an autoscaleInfo: both
// shade a time range at whatever price scale the candles already chose.
class RegionOverlay {
  constructor(shared) {
    this._sharedData = { chart: null, series: null, vertical: "pane", theme: readTheme(), ...shared }
    this._paneViews = [ new RegionPaneView(this._sharedData) ]
    this._attachedParams = null
    this._onThemeChanged = () => {
      this._sharedData.theme = readTheme()
      this.updateAllViews()
      if (this._attachedParams) this._attachedParams.requestUpdate()
    }
  }

  attached(param) {
    this._attachedParams = param
    this._sharedData.chart = param.chart
    this._sharedData.series = param.series
    this._sharedData.theme = readTheme()
    document.addEventListener("theme:changed", this._onThemeChanged)
    this.updateAllViews()
    param.requestUpdate()
  }

  detached() {
    document.removeEventListener("theme:changed", this._onThemeChanged)
    this._sharedData.chart = null
    this._sharedData.series = null
    this._attachedParams = null
  }

  updateAllViews() {
    this._paneViews.forEach(pv => pv.update(this._sharedData))
  }

  paneViews() {
    return this._paneViews
  }
}

export class VcpRunupOverlay extends RegionOverlay {
  // @param from/to       ISO dates bounding the measured run-up window
  // @param pct        excess return over the benchmark, as a fraction
  // @param lateStage  the detector's own late_stage component — true only
  //                   when the penalty was actually applied
  // @param baseStart  ISO date of the base's first bar. The measured window
  //                   ends ON that bar (its close is the return's end point),
  //                   but the base band owns it visually: two translucent
  //                   fills stacked on one bar read as a marked event that
  //                   isn't there. So the fill stops at the base band's left
  //                   edge and the two regions abut. The label's number still
  //                   covers the whole window.
  // @param data       the bars on the chart
  constructor({ from, to, pct, lateStage, baseStart, data }) {
    const late = lateStage === true
    super({
      from: from || null,
      to: to || null,
      stopBefore: baseStart || null,
      data: data || [],
      fill: late ? RUNUP_FILL.late : RUNUP_FILL.normal,
      ink: late ? RUNUP_INK.late : RUNUP_INK.normal,
      label: runupLabel(pct),
      labelY: 12
    })
  }
}

export class VcpEdgeVolumeOverlay extends RegionOverlay {
  // @param from/to  ISO dates bounding the right-edge volume window
  // @param ratio    the 10-bar / 50-bar average volume ratio
  // @param band     [low, high] the calibrated sweet spot
  // @param data     the bars on the chart
  constructor({ from, to, ratio, band, data }) {
    const inBand = isInBand(ratio, band)
    super({
      from: from || null,
      to: to || null,
      data: data || [],
      fill: inBand ? EDGE_VOL_FILL.inBand : EDGE_VOL_FILL.outOfBand,
      ink: inBand ? EDGE_VOL_INK.inBand : EDGE_VOL_INK.outOfBand,
      label: edgeVolLabel(ratio),
      vertical: "volume"
    })
  }
}
